// Port of Weather/LapseRate.swift and Weather/WeatherLocationPrecision.swift.

import type { GeoCoordinate } from '../core/models';

const standardLapseRate = 0.0065;

/**
 * Adjusts air temperature for altitude with a constant environmental lapse rate (6.5 °C per 1000 m). A first-order
 * correction used where no downscaled value exists: synthetic demo forecasts and cached forecasts re-used at a nearby,
 * different altitude.
 */
export const LapseRate = {
  /** Standard environmental lapse rate, °C per metre. */
  standard: standardLapseRate,

  /** Temperature (°C) at altitude `to` given `temperature` at altitude `from` (m). Non-finite inputs: unchanged. */
  adjust(temperature: number, from: number, to: number, rate: number = standardLapseRate): number {
    if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(rate)) return temperature;
    return temperature - rate * (to - from);
  },
};

/** Swift `Double.rounded()`: half away from zero (Math.round rounds half up, -2.5 → -2). */
export function roundHalfAwayFromZero(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

const scale = 100;

/**
 * How precisely a location leaves the device (and is kept) for weather: 2 decimal degrees, about 1.1 km north–south
 * and 0.75 km east–west at 46° N — a coarse location (iOS DECISION.md #8). Used for every forecast request, for the
 * location recorded in the forecast and for WeatherCache keys, so that one transmitted cell is exactly one cache key.
 * The elevation leaves the device rounded to 10 m.
 */
export const WeatherLocationPrecision = {
  /** Decimal places of latitude and longitude. */
  decimals: 2,
  /** Size of one cell in degrees (0.01°). */
  cellDegrees: 1 / scale,
  /** Step (m) to which a transmitted elevation is rounded. */
  elevationStep: 10,

  /** The elevation as sent to a weather provider; undefined when unknown or non-finite. `+ 0` turns -0 into 0. */
  roundedElevation(elevation: number | undefined): number | undefined {
    if (elevation === undefined || !Number.isFinite(elevation)) return undefined;
    const step = WeatherLocationPrecision.elevationStep;
    return roundHalfAwayFromZero(elevation / step) * step + 0;
  },

  /** Rounds both coordinates to 2 places (half away from zero). */
  rounded(location: GeoCoordinate): GeoCoordinate {
    return {
      latitude: WeatherLocationPrecision.roundedDegrees(location.latitude),
      longitude: WeatherLocationPrecision.roundedDegrees(location.longitude),
    };
  },

  /** One coordinate rounded to 2 places. */
  roundedDegrees(degrees: number): number {
    return roundHalfAwayFromZero(degrees * scale) / scale + 0;
  },

  /** "46.50", "-0.10" — a rounded coordinate as text (never locale-dependent). */
  format(degrees: number): string {
    return WeatherLocationPrecision.roundedDegrees(degrees).toFixed(WeatherLocationPrecision.decimals);
  },
};
